import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Button, Checkbox } from "@material-ui/core";
import moment from "moment";
import ReactTable from "../ReactTable/TableVisitRouteSticky3";
import CreateStockCountDialog from "./CreateStockCountDialog";
import EditStockCountDialog from "./EditStockCountDialog";
import StockCountDeleteDialog from "./StockCountDeleteDialog";
import StockCountLogDialog from "./StockCountLogDialog";
import StockCountCopyDialog from "./StockCountCopyDialog";
import { ExcelGenerator } from "utils/excel/ExcelGenerator";
import { ExcelUtils } from "utils/excel/ExcelUtils";
import { translator } from "../../utils/Translator";

// --- StockCountExcelExporter class ---
class StockCountExcelExporter {
  constructor(
    options = {
      items: [],
      sheetName: "StockCount",
      fileName: "stock_count_report.xlsx",
      translate: {},
    }
  ) {
    this.options = options;
    this.translate = options.translate || {};
  }

  async export() {
    try {
      const { fileName, sheetName } = this.options;
      if (!this.options.items || this.options.items.length === 0) {
        throw new Error("No data to export");
      }

      const genOptions = ExcelUtils.getDefaultOptions();
      genOptions.sheetName = sheetName;
      genOptions.header = this.genHeaderOption();
      genOptions.items = this.genItemsOptions();


      const excelGenerator = new ExcelGenerator(genOptions);
      const excelDocument = await excelGenerator.generate();
      await ExcelUtils.download(excelDocument, { fileName });

      return true;
    } catch (error) {
      console.error(" Error generating Excel file:", error.message);
      throw error;
    }
  }

  genItemsOptions() {
    const bodyItems = [];
    let seqNo = 0;

    for (const item of this.options.items) {
      seqNo++;
      const bodyItem = [];

      bodyItem[0] = seqNo.toString(); // ลำดับ
      bodyItem[1] =
        item.docDate && moment(item.docDate).isValid()
          ? moment(item.docDate).format("DD/MM/YYYY")
          : ""; // วันที่เอกสาร
      bodyItem[2] = item.status || translator(this.translate.ISA_Draft, "ร่าง"); // สถานะ
      bodyItem[3] = item.docNo || ""; // เลขที่เอกสาร
      bodyItem[4] = item.salesman || ""; // พนักงานขาย
      bodyItem[5] = item.customer || ""; // ลูกค้า
      bodyItem[6] =
        item.realDate && moment(item.realDate).isValid()
          ? moment(item.realDate).format("DD/MM/YYYY")
          : ""; // วันที่นับจริง
      
      bodyItem[7] = item.Warehouse || ""; // คลังสินค้า
      bodyItem[8] = item.CountMethod || ""; // วิธีการนับ
      bodyItem[9] = item.DocRemark || ""; // หมายเหตุ

      bodyItems.push(bodyItem);
    }

    const genItems = [];
    for (const item of bodyItems) {
      const genRow = item.map((label, index) => ({
        seqNo: index + 1,
        label,
      }));
      genItems.push(genRow);
    }

    return genItems;
  }

  genHeaderOption() {
    const t = this.translate;
    const headers = [
      { seqNo: 1, label: translator(t.ISA_Sequence, "ลำดับ"), width: 8, format: "0" },
      { seqNo: 2, label: translator(t.ISA_DocumentDate, "วันที่เอกสาร"), width: 15 },
      { seqNo: 3, label: translator(t.ISA_Status, "สถานะ"), width: 15 },
      { seqNo: 4, label: translator(t.ISA_DocNumber, "เลขที่เอกสาร"), width: 20 },
      { seqNo: 5, label: translator(t.ISA_Salesman, "พนักงานขาย"), width: 25 },
      { seqNo: 6, label: translator(t.ISA_Customer, "ลูกค้า"), width: 25 },
      { seqNo: 7, label: translator(t.ISA_ActualCountDate, "วันที่นับจริง"), width: 15 },
      { seqNo: 8, label: translator(t.ISA_Warehouse, "คลังสินค้า"), width: 30 },
      { seqNo: 9, label: translator(t.ISA_CountMethod, "วิธีการนับ"), width: 30 },
      { seqNo: 10, label: translator(t.ISA_Remark, "หมายเหตุ"), width: 30 },
    ];

    return headers;
  }
}

// --- StockCountDetailExcelExporter class ---
class StockCountDetailExcelExporter {
  constructor(
    options = {
      items: [],
      sheetName: "StockCountDetail",
      fileName: "stock_count_detail_report.xlsx",
      translate: {},
    }
  ) {
    this.options = options;
    this.translate = options.translate || {};
  }

  async export() {
    try {
      const { fileName, sheetName } = this.options;
      if (!this.options.items || this.options.items.length === 0) {
        throw new Error("No data to export");
      }

      const genOptions = ExcelUtils.getDefaultOptions();
      genOptions.sheetName = sheetName;
      genOptions.header = this.genHeaderOption();
      genOptions.items = this.genItemsOptions();


      const excelGenerator = new ExcelGenerator(genOptions);
      const excelDocument = await excelGenerator.generate();
      await ExcelUtils.download(excelDocument, { fileName });

      return true;
    } catch (error) {
      console.error(" Error generating Excel Detail file:", error.message);
      throw error;
    }
  }

  genItemsOptions() {
    const bodyItems = [];
    let seqNo = 0;

    for (const item of this.options.items) {
      seqNo++;
      const bodyItem = [];

      bodyItem[0] = seqNo.toString(); // ลำดับ
      bodyItem[1] =
        item.docDate && moment(item.docDate).isValid()
          ? moment(item.docDate).format("DD/MM/YYYY")
          : ""; // วันที่เอกสาร
      bodyItem[2] = item.status || translator(this.translate.ISA_Draft, "ร่าง"); // สถานะ
      bodyItem[3] = item.docNo || ""; // เลขที่เอกสาร
      bodyItem[4] = item.salesman || ""; // พนักงานขาย
      bodyItem[5] = item.customer || ""; // ลูกค้า
      bodyItem[6] =
        item.realDate && moment(item.realDate).isValid()
          ? moment(item.realDate).format("DD/MM/YYYY")
          : ""; // วันที่นับจริง
      
      bodyItem[7] = item.Warehouse || ""; // คลังสินค้า
      bodyItem[8] = item.CountMethod || ""; // วิธีการนับ
      bodyItem[9] = item.DocRemark || ""; // หมายเหตุ
      
      // เพิ่มรายละเอียดสินค้า
      bodyItem[10] = item.itemNumber || ""; // ลำดับรายการ
      bodyItem[11] = item.ProductCode || ""; // รหัสสินค้า
      bodyItem[12] = item.ProductName || ""; // ชื่อสินค้า
      bodyItem[13] = item.ProductQuan || ""; // จำนวน
      bodyItem[14] = item.Unit || ""; // หน่วย
      bodyItem[15] = item.lotNo || ""; // Lot No
      bodyItem[16] =
        item.ItemMFGDate && moment(item.ItemMFGDate).isValid()
          ? moment(item.ItemMFGDate).format("DD/MM/YYYY")
          : ""; // วันที่ผลิต
      bodyItem[17] =
        item.ItemEXPDate && moment(item.ItemEXPDate).isValid()
          ? moment(item.ItemEXPDate).format("DD/MM/YYYY")
          : ""; // วันที่หมดอายุ
      bodyItem[18] = item.Cost1 || 0; // ต้นทุน 1
      bodyItem[19] = item.Cost2 || 0; // ต้นทุน 2
      bodyItem[20] = item.Cost3 || 0; // ต้นทุน 3
      bodyItem[21] = item.Cost4 || 0; // ต้นทุน 4

      bodyItems.push(bodyItem);
    }

    const genItems = [];
    for (const item of bodyItems) {
      const genRow = item.map((label, index) => ({
        seqNo: index + 1,
        label,
      }));
      genItems.push(genRow);
    }

    return genItems;
  }

  genHeaderOption() {
    const t = this.translate;
    const headers = [
      { seqNo: 1, label: translator(t.ISA_Sequence, "ลำดับ"), width: 8, format: "0" },
      { seqNo: 2, label: translator(t.ISA_DocumentDate, "วันที่เอกสาร"), width: 15 },
      { seqNo: 3, label: translator(t.ISA_Status, "สถานะ"), width: 15 },
      { seqNo: 4, label: translator(t.ISA_DocNumber, "เลขที่เอกสาร"), width: 20 },
      { seqNo: 5, label: translator(t.ISA_Salesman, "พนักงานขาย"), width: 25 },
      { seqNo: 6, label: translator(t.ISA_Customer, "ลูกค้า"), width: 25 },
      { seqNo: 7, label: translator(t.ISA_ActualCountDate, "วันที่นับจริง"), width: 15 },
      { seqNo: 8, label: translator(t.ISA_Warehouse, "คลังสินค้า"), width: 30 },
      { seqNo: 9, label: translator(t.ISA_CountMethod, "วิธีการนับ"), width: 30 },
      { seqNo: 10, label: translator(t.ISA_Remark, "หมายเหตุ"), width: 30 },
      { seqNo: 11, label: translator(t.ISA_ItemNumber, "ลำดับรายการ"), width: 12 },
      { seqNo: 12, label: translator(t.ISA_ProductCode, "รหัสสินค้า"), width: 20 },
      { seqNo: 13, label: translator(t.ISA_ProductName, "ชื่อสินค้า"), width: 35 },
      { seqNo: 14, label: translator(t.ISA_Qty, "จำนวน"), width: 12, format: "0.00" },
      { seqNo: 15, label: translator(t.ISA_Unit, "หน่วย"), width: 10 },
      { seqNo: 16, label: "Lot No", width: 20 },
      { seqNo: 17, label: translator(t.ISA_MfgDate, "วันที่ผลิต"), width: 15 },
      { seqNo: 18, label: translator(t.ISA_ExpDate, "วันที่หมดอายุ"), width: 15 },
      { seqNo: 19, label: translator(t.ISA_Cost1, "ต้นทุน 1"), width: 15, format: "#,##0.00" },
      { seqNo: 20, label: translator(t.ISA_Cost2, "ต้นทุน 2"), width: 15, format: "#,##0.00" },
      { seqNo: 21, label: translator(t.ISA_Cost3, "ต้นทุน 3"), width: 15, format: "#,##0.00" },
      { seqNo: 22, label: translator(t.ISA_Cost4, "ต้นทุน 4"), width: 15, format: "#,##0.00" },
    ];

    return headers;
  }
}

// --- HeaderCheckbox Component ---
const HeaderCheckbox = ({ isAllSelected, isIndeterminate, handleSelectAll }) => {
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = isIndeterminate();
    }
  }, [isIndeterminate]);

  return (
    <Checkbox
      ref={ref}
      checked={isAllSelected()}
      onChange={handleSelectAll}
      inputProps={{ "aria-label": "select all rows" }}
    />
  );
};

const Menu_StockCountTable = () => {
  const { authUser } = useSelector(({ auth }) => auth);
  const { translate } = useSelector(({ i18n }) => i18n);

  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [logDialog, setLogDialog] = useState({
    open: false,
    docNo: null,
  });
  const [editDialog, setEditDialog] = useState({
    open: false,
    data: null,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    docNumbers: [],
    salesmen: [],
    customers: [],
  });
  const [copyDialog, setCopyDialog] = useState({
    open: false,
    data: null,
  });

  const {
    companySelected,
    salesOrgSelected,
    channelSelected,
    startDateSelected,
  } = useSelector(({ main }) => main);
  const { endDateSelected } = useSelector(({ main }) => main);
  const {
    parentSalesmanSelected,
    salesAreaSelected,
    salesmanCodeSelected,
  } = useSelector(({ salesman }) => salesman);
   const { yearSelected, qtrSelected, monthSelected
      , customerGroupSelected, customerCodeSelected, customerProvinceSelected
    } = useSelector(({ customer }) => customer);
  const { docNumberFilter } = useSelector(
    ({ expenseTransaction }) => expenseTransaction
  );
    const { languageSelected } = useSelector(({ settings }) => settings);
  

  // --- Fetch Data ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Build filter strings for company, sales org, channel
      let strCompany = "";
      if (companySelected && companySelected.length > 0) {
        strCompany = companySelected.map(q => q.Division).join(',');
      }

      let strSalesOrg = "";
      if (salesOrgSelected && salesOrgSelected !== null) {
        strSalesOrg = salesOrgSelected.map(q => q.SalesOrg).join(',');
      }

      let strChannel = "";
      if (channelSelected && channelSelected !== null) {
        strChannel = channelSelected.map(q => q.Channel).join(',');
      }

      // Build filter strings for salesman
      let strParentSalesman = "";
      if (parentSalesmanSelected && parentSalesmanSelected.length > 0) {
        strParentSalesman = parentSalesmanSelected.map(q => q.ParentSalesmanCode).join(',');
      }

      let strSalesArea = "";
      if (salesAreaSelected && salesAreaSelected.length > 0) {
        strSalesArea = salesAreaSelected.map(q => q.SalesAreaCode).join(',');
      }

      let strSalesman = "";
      if (salesmanCodeSelected && salesmanCodeSelected.length > 0) {
        strSalesman = salesmanCodeSelected.map(q => q.SalesmanCode).join(',');
      }

      // Build filter strings for customer
      let strCustomerGroup = "";
      if (customerGroupSelected && customerGroupSelected !== null) {
        strCustomerGroup = customerGroupSelected.map(q => q.CustomerGroupCode).join(',');
      }

      let strCustomerCode = "";
      if (customerCodeSelected && customerCodeSelected !== null) {
        strCustomerCode = customerCodeSelected.map(q => q.CustomerCode).join(',');
      }

      let strCustomerProvince = "";
      if (customerProvinceSelected && customerProvinceSelected !== null) {
        strCustomerProvince = customerProvinceSelected.map(q => q.ProvinceCode).join(',');
      }

      const url = `${process.env.REACT_APP_API_URL}StockCount/getStockCountTransaction`;
      const params = {
        company: authUser?.company || "DEFAULT",
        salesmanCode: authUser?.salesman || authUser?.username || "DEFAULT",
        startDate: startDateSelected,
        endDate: endDateSelected,
        docNumber: docNumberFilter || "",
        com: strCompany,
        salesOrg: strSalesOrg,
        channel: strChannel,
        parentSalesman: strParentSalesman,
        salesArea: strSalesArea,
        salesman: strSalesman,
        CustomerGroup: strCustomerGroup,
        CustomerCode: strCustomerCode,
        ProvinceCode: strCustomerProvince,
        language: languageSelected.id,
      };


      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
        },
      });

      const data = response?.data?.data;

      if (data && Array.isArray(data)) {
        const allData = data.map((item, index) => ({
          id: item.ID || index,
          docDate: item.DocDate
            ? moment(item.DocDate, "YYYYMMDD").format("YYYY-MM-DD")
            : "",
          status: item.Status === "B" ? translator(translate.ISA_Saved, "บันทึกแล้ว") : translator(translate.ISA_Draft, "ร่าง"),
          statusCode: item.Status,
          docNo: item.DocNumber || "",
          salesman: `${item.SalesmanName || ""} (${item.SalesmanCode || ""})`,
          customer: `${item.CustomerName || ""} (${item.CustomerCode || ""})`,
          realDate: item.DocRefDate
            ? moment(item.DocRefDate, "YYYYMMDD").format("YYYY-MM-DD")
            : "",
          DocRemark: item.DocRemark || "",
          Warehouse: item.Warehouse || "",
          CountMethod: item.CountMethod || "",
          log: item.log || true,
          Filename: item.Filename,
          customercode: item.CustomerCode,
          ProductCode: item.ProductCode,

          itemNumber: item.ItemNumber || "",
          ProductQuan: item.ProductQuan || "",
          ItemMFGDate: item.ItemMFGDate 
            ? moment(item.ItemMFGDate, "YYYYMMDD").format("YYYY-MM-DD")
            : "",
          ItemEXPDate: item.ItemEXPDate
            ? moment(item.ItemEXPDate, "YYYYMMDD").format("YYYY-MM-DD")
            : "",
          Unit: item.Unit || "",
          ProductName: item.ProductName || translator(translate.ISA_ProductNameNotSpecified, "ไม่ระบุชื่อสินค้า"),
          Cost1: item.Cost1 || 0,
          Cost2: item.Cost2 || 0,
          Cost3: item.Cost3 || 0,
          Cost4: item.Cost4 || 0,
          lotNo: item.Lot|| item.LotNo || "",
        }));


        allData.sort((a, b) => new Date(b.docDate) - new Date(a.docDate));
        setDataTable(allData);
      } else {
        setDataTable([]);
      }
    } catch (error) {
      console.error("API call failed:", error.response || error.message);
      setDataTable([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authUser) {
      fetchData();
    }
  }, [
    authUser,
    startDateSelected,
    endDateSelected,
    docNumberFilter,
    parentSalesmanSelected,
    salesAreaSelected,
    salesmanCodeSelected,
    companySelected,
    salesOrgSelected,
    channelSelected
      , customerGroupSelected, customerCodeSelected, customerProvinceSelected,
      languageSelected,
  ]);

  // --- Handle Excel Download ---
  const handleExcelDownload = async () => {
    try {
      if (!dataTable || dataTable.length === 0) {
        alert(translator(translate.ISA_NoDataToExport, " ไม่มีข้อมูลสำหรับส่งออกไปยัง Excel"));
        return;
      }


      const exporter = new StockCountExcelExporter({
        items: dataTable,
        sheetName: "StockCount",
        fileName: `stock_count_report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`,
        translate: translate,
      });

      await exporter.export();
      alert(translator(translate.ISA_ExcelExportSuccess, " ส่งออกไฟล์ Excel สำเร็จ"));
    } catch (error) {
      console.error(" Failed to export Excel:", error.message);
      alert(translator(translate.ISA_ExcelExportError, " เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel: ") + error.message);
    }
  };

  // --- Handle Excel Detail Download ---
  const handleExcelDetailDownload = async () => {
    try {
      if (!dataTable || dataTable.length === 0) {
        alert(translator(translate.ISA_NoDataToExport, " ไม่มีข้อมูลสำหรับส่งออกไปยัง Excel Detail"));
        return;
      }


      const exporter = new StockCountDetailExcelExporter({
        items: dataTable,
        sheetName: "StockCountDetail",
        fileName: `stock_count_detail_report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`,
        translate: translate,
      });

      await exporter.export();
      alert(translator(translate.ISA_ExcelExportSuccess, " ส่งออกไฟล์ Excel Detail สำเร็จ"));
    } catch (error) {
      console.error(" Failed to export Excel Detail:", error.message);
      alert(translator(translate.ISA_ExcelExportError, " เกิดข้อผิดพลาดในการส่งออกไฟล์ Excel Detail: ") + error.message);
    }
  };

  const handleAddClick = () => setDialogOpen(true);

  const handleDialogClose = (wasSuccess = false) => {
    setDialogOpen(false);
    if (wasSuccess) fetchData();
  };

  const handleDeleteClick = (rowData) => {
    setDeleteDialog({
      open: true,
      docNumbers: [rowData.docNo],
      salesmen: [rowData.salesman],
      customers: [rowData.customer],
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      alert(translator(translate.ISA_PleaseSelectItemsToDelete, "กรุณาเลือกรายการที่ต้องการลบ"));
      return;
    }
    const selectedData = dataTable.filter((row) => selectedRows.includes(row.docNo));
    setDeleteDialog({
      open: true,
      docNumbers: selectedRows,
      salesmen: selectedData.map((row) => row.salesman),
      customers: selectedData.map((row) => row.customer),
    });
  };

  const handleDeleteConfirm = (deletedDocNumbers) => {
    setDataTable((prevData) =>
      prevData.filter((row) => !deletedDocNumbers.includes(row.docNo))
    );
    setSelectedRows((prevSelected) =>
      prevSelected.filter((docNo) => !deletedDocNumbers.includes(docNo))
    );
    setDeleteDialog({ open: false, docNumbers: [], salesmen: [], customers: [] });

    // Refresh data after delete confirmation
    fetchData();
  };

  const handleDialogSubmit = (newData) => {
    setDataTable((prevData) =>
      [newData, ...prevData].map((item, index) => ({ ...item, no: index + 1 }))
    );
  };

  const handleEdit = (rowData) => setEditDialog({ open: true, data: rowData });


  const handleSelectRow = (docNo) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(docNo)
        ? prevSelected.filter((id) => id !== docNo)
        : [...prevSelected, docNo]
    );
  };

  const isRowSelected = (docNo) => selectedRows.includes(docNo);

  const isAllSelected = () =>
    dataTable.length > 0 && selectedRows.length === dataTable.length;

  const isIndeterminate = () =>
    selectedRows.length > 0 && selectedRows.length < dataTable.length;

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(dataTable.map((row) => row.docNo));
    } else {
      setSelectedRows([]);
    }
  };

  const generateStockCountPDF = (rowData) => {
    const docID = rowData.docNo;
    const custCode = rowData.customercode || "";
    const salesCode = rowData.SalesmanCode || "";
    const company = authUser?.company || "DEFAULT";
    const username = authUser?.username && authUser.username !== "#" ? authUser.username : "";
    const Filename = rowData.Filename;

    const reportUrl = `https://onlineqa.ismartsales.com/webqa/Report_Report_Stock.aspx?Filename=${Filename}&DocID=${docID}&Company=${company}`;

    window.open(reportUrl, "_blank", "noopener,noreferrer");
  };

  const handleLogClick = (docNo) => {
    setLogDialog({ open: true, docNo: docNo });
  };

  const handleCopyClick = (rowData) => {
    setCopyDialog({ open: true, data: rowData });
  };

  const handleCopyConfirm = (result) => {
    if (result.success && result.shouldRefresh) {
      fetchData();
    }
    setCopyDialog({ open: false, data: null });
  };

  // --- Columns ---
  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div className="text-center">
            <HeaderCheckbox
              isAllSelected={isAllSelected}
              isIndeterminate={isIndeterminate}
              handleSelectAll={handleSelectAll}
            />
          </div>
        ),
        id: "selection",
        width: 50,
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="text-center">
            <Checkbox
              checked={isRowSelected(row.original.docNo)}
              color="primary"
              onChange={() => handleSelectRow(row.original.docNo)}
              onClick={(e) => e.stopPropagation()}
              inputProps={{ "aria-label": `select row ${row.original.docNo}` }}
            />
          </div>
        ),
      },
      {
        Header: () => <div className="text-center">{translator(translate.ISA_Sequence, "#")}</div>,
        accessor: "no",
        width: 60,
        disableSortBy: true,
        Cell: ({ row }) => <div className="text-center">{row.index + 1}</div>,
      },
      {
        Header: () => <div className="text-center">{translator(translate.ISA_Action, "การดำเนินการ")}</div>,
        accessor: "action",
        width: 300,
        Cell: ({ row }) => (
          <div className="text-center">
            <button
              className="jr-btn jr-btn-md"
              style={{ background: "transparent", border: "none" }}
              onClick={() => handleEdit(row.original)}
              title={translator(translate.ISA_Edit, "แก้ไข")}
            >
              <i className="fa-solid fa-pen-to-square" style={{ fontSize: 24 }}></i>
            </button>
            <button
              className="jr-btn jr-btn-md"
              style={{ background: "transparent", border: "none" }}
              title={translator(translate.ISA_TooltipAttechFile, "แนบไฟล์")}
            >
              <i className="fa-solid fa-paperclip" style={{ fontSize: 20 }}></i>
            </button>
            <button
              className="jr-btn jr-btn-md"
              style={{ background: "transparent", border: "none" }}
              onClick={() => handleCopyClick(row.original)}
              title={translator(translate.ISA_TooltipCopy, "คัดลอก")}
            >
              <i className="zmdi zmdi-copy" style={{ fontSize: 20 }} />
            </button>
            <button
              className="jr-btn jr-btn-md"
              title={translator(translate.ISA_Expense_CreatePdf, "สร้างไฟล์ PDF")}
              style={{ background: "transparent", border: "none", color: "#131212ff" }}
              onClick={() => generateStockCountPDF(row.original)}
            >
              <i className="zmdi zmdi-collection-pdf" style={{ fontSize: 20 }} />
            </button>
            <button
              className="jr-btn jr-btn-md"
              style={{ background: "transparent", border: "none" }}
              onClick={() => handleDeleteClick(row.original)}
              title={translator(translate.ISA_Delete, "ลบ")}
            >
              <i className="fas fa-trash" style={{ fontSize: 24 }}></i>
            </button>
          </div>
        ),
      },
      {
        Header: () => <div className="text-center">{translator(translate.ISA_Expense_DocDate, "วันที่เอกสาร")}</div>,
        accessor: "docDate",
        width: 150,
        Cell: ({ value }) => (
          <div className="text-center">
            {value ? moment(value).format("DD MMM YYYY") : ""}
          </div>
        ),
      },
      {
        Header: () => <div className="text-center">{translator(translate.ISA_DocStatus, "สถานะ")}</div>,
        accessor: "status",
        width: 150,
        Cell: ({ value, row }) => (
          <div className="text-center">
            {row.original.statusCode === "B" ? (
              <button
                className="jr-btn jr-btn-sm"
                style={{
                  background: "#28a745",
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "default",
                  fontSize: "12px",
                }}
              >
                {value}
              </button>
            ) : (
              <button
                className="jr-btn jr-btn-sm"
                style={{
                  background: "#ffc107",
                  color: "#000",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "default",
                  fontSize: "12px",
                }}
              >
                {value}
              </button>
            )}
          </div>
        ),
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_DocNumber, "เลขที่เอกสาร")}</div>,
        accessor: "docNo",
        width: 180,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_Salesman, "พนักงานขาย")}</div>,
        accessor: "salesman",
        width: 250,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_Customer, "ลูกค้า")}</div>,
        accessor: "customer",
        width: 250,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      {
        Header: () => <div className="text-center">{translator(translate.ISA_StockCountDate, "วันที่นับจริง")}</div>,
        accessor: "realDate",
        width: 150,
        Cell: ({ value }) => (
          <div className="text-center">
            {value ? moment(value).format("DD MMM YYYY") : ""}
          </div>
        ),
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_Warehouse, "คลังสินค้า")}</div>,
        accessor: "Warehouse",
        minWidth: 200,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_CountMethod, "วิธีการนับ")}</div>,
        accessor: "CountMethod",
        minWidth: 200,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      {
        Header: () => <div className="text-left">{translator(translate.ISA_Remark, "หมายเหตุ")}</div>,
        accessor: "DocRemark",
        minWidth: 200,
        Cell: ({ value }) => <div className="text-left">{value}</div>,
      },
      
      {
        Header: () => <div className="text-center">{translator(translate.ISA_AuditLog, "Audit Log")}</div>,
        accessor: "log",
        width: 170,
        Cell: ({ row }) => (
          <div className="text-center">
            {row.original.log && (
              <button
                className="jr-btn jr-btn-md bg-success text-white"
                onClick={() => handleLogClick(row.original.docNo)}
                title={translator(translate.ISA_AuditLog, "ดู Audit Log")}
              >
                <i className="zmdi zmdi-file-text" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [isAllSelected, isIndeterminate, selectedRows, dataTable, translate]
  );

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span>{translator(translate.ISA_StockCount, "ใบนับสต็อก")}</span>
        <div>
          <Button
            variant="contained"
            color="primary"
            style={{ marginRight: 8 }}
            onClick={handleAddClick}
          >
            {translator(translate.ISA_Add, "เพิ่ม")}
          </Button>
          {selectedRows.length > 0 && (
            <Button
              variant="contained"
              style={{ background: "#e53935", color: "#fff", marginRight: 8 }}
              onClick={handleBulkDelete}
            >
              {translator(translate.ISA_ButtonDeleteSelected, "ลบรายการที่เลือก")} ({selectedRows.length})
            </Button>
          )}
          <Button
            variant="contained"
            style={{ background: "#43b02a", color: "#fff", marginRight: 8 }}
            onClick={handleExcelDownload}
          >
            {translator(translate.ISA_ExportExcel, "Export Excel")}
          </Button>
          <Button
            variant="contained"
            style={{ background: "#43b02a", color: "#fff" }}
            onClick={handleExcelDetailDownload}
          >
            {translator(translate.ISA_ButtonExportExcelDetail, "Export Excel Detail")}
          </Button>
        </div>
      </div>
      <div className="card-body">
        <ReactTable
          columns={columns}
          data={dataTable}
          loading={loading}
          initialPageSize={20}
        />
        {dataTable.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "20px", color: "#888" }}>
            {translator(translate.ISA_NoDataFound, "ไม่พบข้อมูลใบนับสต็อก")}
          </div>
        )}
      </div>

      <StockCountDeleteDialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({ open: false, docNumbers: [], salesmen: [], customers: [] })
        }
        onConfirm={handleDeleteConfirm}
        docNumbers={deleteDialog.docNumbers}
        salesmen={deleteDialog.salesmen}
        customers={deleteDialog.customers}
      />

      <CreateStockCountDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
      />

      <StockCountLogDialog
        open={logDialog.open}
        docNo={logDialog.docNo}
        companyCode={authUser.company}
        onClose={() => setLogDialog({ open: false, docNo: null })}
      />

      {editDialog.data && (
        <EditStockCountDialog
          open={editDialog.open}
          initialData={editDialog.data}
          onClose={(wasSuccessful) => {
            setEditDialog({ open: false, data: null });
            if (wasSuccessful) {
              fetchData(); // รีเฟรชข้อมูลในตาราง
              setTimeout(() => {
                window.scrollTo(0, 0); // รีเซ็ต scroll bar
              }, 100);
            }
          }}
        />
      )}

      <StockCountCopyDialog
        open={copyDialog.open}
        onClose={() => setCopyDialog({ open: false, data: null })}
        onConfirm={handleCopyConfirm}
        data={copyDialog.data}
      />
    </div>
  );
};

export default Menu_StockCountTable;